import React, {
  CSSProperties,
  useEffect,
  useState,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTenantUserInfo } from '../../context/userInfoProvider';

type LocationState = {
  selected: string[] | string;
  type: 'DELIVERY' | 'PICKUP' | string;
  id?: string[];
  needHelp?: string;
  needPack?: string;
  moverOption?: string;
  dateSelected: string;
  timeSelected: string;
};

const pageContainer: CSSProperties = {
  minHeight: '100vh',
  backgroundColor: '#261136',
  padding: 24,
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  fontFamily:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
};

const shellCard: CSSProperties = {
  width: '100%',
  maxWidth: 900,
  background:
    'radial-gradient(circle at top, #3A1F52 0, #170824 45%, #12061f 100%)',
  borderRadius: 16,
  padding: 24,
  boxSizing: 'border-box',
  boxShadow: '0 18px 45px rgba(0,0,0,0.45)',
  color: '#F5F5FA',
};

const headerBlock: CSSProperties = {
  marginBottom: 16,
};

const titleStyle: CSSProperties = {
  fontSize: 22,
  fontWeight: 600,
};

const subtitleStyle: CSSProperties = {
  fontSize: 13,
  opacity: 0.8,
};

const labelStyle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  marginBottom: 4,
};

const addressCard: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: '#7B1FA2',
  padding: 12,
  borderRadius: 10,
  margin: '10px 0 8px',
};

const addressText: CSSProperties = {
  fontSize: 15,
  color: '#FFF',
  flex: 1,
  marginRight: 12,
};

const editLink: CSSProperties = {
  fontSize: 14,
  color: '#E4DCFF',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const selectStyle: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #CCCCCC',
  fontSize: 14,
  color: '#111827',
};

const textareaStyle: CSSProperties = {
  width: '100%',
  minHeight: 70,
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid #CCCCCC',
  fontSize: 13,
  resize: 'vertical' as const,
};

const footer: CSSProperties = {
  marginTop: 24,
  display: 'flex',
  justifyContent: 'flex-end',
};

const primaryButton: CSSProperties = {
  padding: '10px 16px',
  borderRadius: 6,
  border: 'none',
  backgroundColor: '#7B1FA2',
  color: '#FFF',
  fontSize: 14,
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButton: CSSProperties = {
  ...primaryButton,
  backgroundColor: 'transparent',
  border: '1px solid rgba(255,255,255,0.4)',
};

const addressFormGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 12,
  marginTop: 10,
};

const inputStyle: CSSProperties = {
  width: '100%',
  borderRadius: 6,
  border: '1px solid #4826A0',
  padding: '6px 8px',
  backgroundColor: 'rgba(10, 4, 18, 0.9)',
  color: '#F5F5FA',
  fontSize: 14,
  boxSizing: 'border-box',
};

const actionRow: CSSProperties = {
  marginTop: 12,
  display: 'flex',
  gap: 8,
};

const statusText: CSSProperties = {
  fontSize: 12,
  marginTop: 8,
  opacity: 0.9,
};

const TenantConfirmAddressPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState | undefined>();
  const state = location.state;

  const tenantUser = useTenantUserInfo() as any;

  const {
    fetchData,
    loading: userLoading,
    email,
    addressLine1,
    addressLine2,
    city,
    state: region,
    zip,
    specialInstructions: addressSpecialInstructions,
    building,
    parking,
    updateUserAddress,
  } = tenantUser || {};

  const [moverOption, setMoverOption] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');

  // local edit state for address
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [savingAddress, setSavingAddress] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const [addressInput, setAddressInput] = useState({
    addressLine1: addressLine1 || '',
    addressLine2: addressLine2 || '',
    city: city || '',
    state: region || '',
    zip: zip || '',
    specialInstructions: addressSpecialInstructions || '',
    building: building || '',
    parking: parking || '',
  });

  // load user info when we land on this page
  useEffect(() => {
    const load = async () => {
      try {
        if (fetchData) {
          await fetchData();
        }
      } catch (e) {
        console.error('Error fetching user info', e);
      }
    };
    load();
  }, [fetchData]);

  // keep local form in sync when context updates
  useEffect(() => {
    setAddressInput({
      addressLine1: addressLine1 || '',
      addressLine2: addressLine2 || '',
      city: city || '',
      state: region || '',
      zip: zip || '',
      specialInstructions: addressSpecialInstructions || '',
      building: building || '',
      parking: parking || '',
    });
  }, [
    addressLine1,
    addressLine2,
    city,
    region,
    zip,
    addressSpecialInstructions,
    building,
    parking,
  ]);

  if (!state) {
    return (
      <div style={pageContainer}>
        <div style={shellCard}>
          <h2>Missing order details</h2>
          <p>Please start again from &quot;Schedule appointment&quot;.</p>
        </div>
      </div>
    );
  }

  const fullAddress =
    addressLine1 || addressLine2 || city || region || zip
      ? `${addressLine1 ?? ''}${
          addressLine2 ? ', ' + addressLine2 : ''
        }, ${city ?? ''}, ${region ?? ''} ${zip ?? ''}`.trim()
      : 'No address on file yet.';

  const isDelivery = state.type === 'DELIVERY';

  const handleSaveAddress = async () => {
    try {
      const {
        addressLine1: l1,
        city: c,
        state: st,
        zip: z,
        building: b,
        parking: p,
      } = addressInput;

      if (!l1 || !c || !st || !z || !b || !p) {
        setStatus('Please fill in all required address fields.');
        return;
      }

      if (!updateUserAddress || !email) {
        setStatus('Unable to update address right now.');
        return;
      }

      setSavingAddress(true);
      setStatus(null);

      await updateUserAddress(email, {
        ...addressInput,
        zip: z,
      });

      setStatus('Address updated.');
      setIsEditingAddress(false);
    } catch (err) {
      console.error(err);
      setStatus('There was a problem saving your address.');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleReview = () => {
    const moverOptionString = isDelivery
      ? `${moverOption || ''}; ${deliveryInstructions || ''}`.trim()
      : '';

    history.push('/tenant/neworder/review', {
      ...state,
      moverOption: moverOptionString,
    });
  };

  const isBusy = userLoading || savingAddress;

  return (
    <div style={pageContainer}>
      <div style={shellCard}>
        <div style={headerBlock}>
          <h1 style={titleStyle}>Confirm address</h1>
          <div style={subtitleStyle}>
            We&apos;ll use this address for your upcoming pickup or delivery.
          </div>
        </div>

        <div>
          <div style={labelStyle}>Address</div>
          <div style={addressCard}>
            <div style={addressText}>
              {userLoading ? 'Loading address…' : fullAddress}
            </div>
            <div
              style={{
                ...editLink,
                opacity: isBusy ? 0.6 : 1,
                cursor: isBusy ? 'default' : 'pointer',
              }}
              onClick={() => {
                if (!isBusy) {
                  setIsEditingAddress(prev => !prev);
                  setStatus(null);
                }
              }}
            >
              {isEditingAddress ? 'Close' : 'Edit'}
            </div>
          </div>

          {isEditingAddress && (
            <>
              <div style={addressFormGrid}>
                <div>
                  <div style={labelStyle}>Address line 1 *</div>
                  <input
                    style={inputStyle}
                    value={addressInput.addressLine1}
                    onChange={e =>
                      setAddressInput(prev => ({
                        ...prev,
                        addressLine1: e.target.value,
                      }))
                    }
                    disabled={savingAddress}
                  />
                </div>

                <div>
                  <div style={labelStyle}>Address line 2</div>
                  <input
                    style={inputStyle}
                    value={addressInput.addressLine2}
                    onChange={e =>
                      setAddressInput(prev => ({
                        ...prev,
                        addressLine2: e.target.value,
                      }))
                    }
                    disabled={savingAddress}
                  />
                </div>

                <div>
                  <div style={labelStyle}>City *</div>
                  <input
                    style={inputStyle}
                    value={addressInput.city}
                    onChange={e =>
                      setAddressInput(prev => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    disabled={savingAddress}
                  />
                </div>

                <div>
                  <div style={labelStyle}>State *</div>
                  <input
                    style={inputStyle}
                    value={addressInput.state}
                    onChange={e =>
                      setAddressInput(prev => ({
                        ...prev,
                        state: e.target.value,
                      }))
                    }
                    disabled={savingAddress}
                  />
                </div>

                <div>
                  <div style={labelStyle}>ZIP *</div>
                  <input
                    style={inputStyle}
                    value={addressInput.zip}
                    onChange={e =>
                      setAddressInput(prev => ({
                        ...prev,
                        zip: e.target.value,
                      }))
                    }
                    disabled={savingAddress}
                  />
                </div>

                <div>
                  <div style={labelStyle}>Special instructions</div>
                  <input
                    style={inputStyle}
                    value={addressInput.specialInstructions}
                    onChange={e =>
                      setAddressInput(prev => ({
                        ...prev,
                        specialInstructions: e.target.value,
                      }))
                    }
                    disabled={savingAddress}
                    placeholder="Gate code, how to find your unit, etc."
                  />
                </div>

                <div>
                  <div style={labelStyle}>Building type *</div>
                  <input
                    style={inputStyle}
                    value={addressInput.building}
                    onChange={e =>
                      setAddressInput(prev => ({
                        ...prev,
                        building: e.target.value,
                      }))
                    }
                    disabled={savingAddress}
                    placeholder="House / Apartment / …"
                  />
                </div>

                <div>
                  <div style={labelStyle}>Where to park *</div>
                  <input
                    style={inputStyle}
                    value={addressInput.parking}
                    onChange={e =>
                      setAddressInput(prev => ({
                        ...prev,
                        parking: e.target.value,
                      }))
                    }
                    disabled={savingAddress}
                    placeholder="Driveway / Parking lot / …"
                  />
                </div>
              </div>

              <div style={actionRow}>
                <button
                  type="button"
                  style={primaryButton}
                  onClick={handleSaveAddress}
                  disabled={savingAddress}
                >
                  {savingAddress ? 'Saving…' : 'Save address'}
                </button>
                <button
                  type="button"
                  style={secondaryButton}
                  onClick={() => {
                    setIsEditingAddress(false);
                    setStatus(null);
                    // reset to context values
                    setAddressInput({
                      addressLine1: addressLine1 || '',
                      addressLine2: addressLine2 || '',
                      city: city || '',
                      state: region || '',
                      zip: zip || '',
                      specialInstructions: addressSpecialInstructions || '',
                      building: building || '',
                      parking: parking || '',
                    });
                  }}
                  disabled={savingAddress}
                >
                  Cancel
                </button>
              </div>

              {status && <div style={statusText}>{status}</div>}
            </>
          )}
        </div>

        {isDelivery && (
          <div style={{ marginTop: 16 }}>
            <div style={labelStyle}>Delivery options</div>
            <select
              value={moverOption}
              onChange={e => setMoverOption(e.target.value)}
              style={selectStyle}
            >
              <option value="">Select an option</option>
              <option value="Leave at Door">Leave at Door</option>
              <option value="Meet Mover">Meet Mover</option>
            </select>

            <div style={{ ...labelStyle, marginTop: 14 }}>
              Special instructions for this delivery (optional)
            </div>
            <textarea
              style={textareaStyle}
              placeholder="Ex: Gate code, where to leave items, etc."
              value={deliveryInstructions}
              onChange={e => setDeliveryInstructions(e.target.value)}
            />
          </div>
        )}

        <div style={footer}>
          <button type="button" style={primaryButton} onClick={handleReview}>
            Review order
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantConfirmAddressPage;
